// src/pages/reseller/ResellerPartnersPage.tsx

import {
  AlertCircle,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  Filter,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  Wallet,
  Wifi,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createResellerPartner,
  getResellerPartners,
  ResellerPartnerSummary,
} from "../reseller/resellerPartnersApi";

function formatCurrency(value?: number | null) {
  return `KES ${Number(value || 0).toLocaleString("en-KE")}`;
}

function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateInput(value?: string | null) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 16);
}

function getStatusTone(status?: string | null) {
  const value = String(status || "").toLowerCase();

  if (value === "active") {
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  }

  if (value === "overdue") {
    return "border-amber-200 bg-amber-100 text-amber-800";
  }

  if (value === "suspended") {
    return "border-rose-200 bg-rose-100 text-rose-700";
  }

  if (value === "inactive") {
    return "border-slate-300 bg-slate-200 text-slate-700";
  }

  return "border-sky-200 bg-sky-100 text-sky-800";
}

function formatStatus(status?: string | null) {
  return String(status || "UNKNOWN")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${getStatusTone(
      status,
    )}`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {formatStatus(status)}
  </span>
);

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading reseller partners…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while reseller accounts and billing details are being
            prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 7 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0
              ? "w-48"
              : index === 2
                ? "w-40"
                : index === 6
                  ? "ml-auto w-20"
                  : "w-28"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-44 rounded bg-slate-200" />
        <div className="h-3 w-32 rounded bg-slate-200" />
      </div>

      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-16 rounded-2xl bg-slate-200" />
      <div className="h-16 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

export default function ResellerPartnersPage() {
  const navigate = useNavigate();

  const [resellers, setResellers] = useState<ResellerPartnerSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    business_name: "",
    contact_person: "",
    phone_number: "",
    email: "",
    location: "",
    billing_started_at: "",
    billing_expires_at: "",
    payment_due_at: "",
    grace_period_days: 3,
    notes: "",
  });

  async function loadResellers() {
    setLoading(true);
    setError(null);

    try {
      const data = await getResellerPartners({
        search: search.trim() || undefined,
        status: status || undefined,
      });

      setResellers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || "Failed to load reseller partners.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadResellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredResellers = useMemo(() => {
    const q = search.trim().toLowerCase();

    return resellers.filter((item) => {
      if (status && item.status !== status) return false;

      if (!q) return true;

      return [
        item.business_name,
        item.contact_person || "",
        item.phone_number || "",
        item.email || "",
        item.location || "",
        item.account_number || "",
        item.username_prefix || "",
        item.status || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [resellers, search, status]);

  const totals = useMemo(() => {
    return filteredResellers.reduce(
      (acc, item) => {
        acc.accounts += item.total_accounts || 0;
        acc.available += item.available_accounts || 0;
        acc.connected += item.connected_accounts || 0;
        acc.bill += item.total_bill || 0;
        acc.paid += item.total_paid || 0;
        acc.balance += item.balance || 0;
        return acc;
      },
      {
        accounts: 0,
        available: 0,
        connected: 0,
        bill: 0,
        paid: 0,
        balance: 0,
      },
    );
  }, [filteredResellers]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();

    if (!form.business_name.trim() || !form.phone_number.trim()) {
      setError("Business name and phone number are required.");
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const payload = {
        ...form,
        business_name: form.business_name.trim(),
        contact_person: form.contact_person.trim() || undefined,
        phone_number: form.phone_number.trim(),
        email: form.email.trim() || undefined,
        location: form.location.trim() || undefined,
        notes: form.notes.trim() || undefined,
        billing_started_at: form.billing_started_at
          ? new Date(form.billing_started_at).toISOString()
          : undefined,
        billing_expires_at: form.billing_expires_at
          ? new Date(form.billing_expires_at).toISOString()
          : undefined,
        payment_due_at: form.payment_due_at
          ? new Date(form.payment_due_at).toISOString()
          : undefined,
      };

      await createResellerPartner(payload);

      setShowCreate(false);
      setForm({
        business_name: "",
        contact_person: "",
        phone_number: "",
        email: "",
        location: "",
        billing_started_at: "",
        billing_expires_at: "",
        payment_due_at: "",
        grace_period_days: 3,
        notes: "",
      });

      await loadResellers();
    } catch (err: any) {
      setError(err?.message || "Failed to create reseller partner.");
    } finally {
      setCreating(false);
    }
  }

  const clearFilters = () => {
    setSearch("");
    setStatus("");
  };

  const hasFilters = Boolean(search || status);

  if (loading && resellers.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Reseller Partner Management
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Reseller Partners
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Manage reseller businesses, PPPoE account inventory, billing
                  periods, payments, and outstanding balances.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={loadResellers}
                  disabled={loading}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {loading ? "Refreshing…" : "Refresh"}
                </button>

                <button
                  type="button"
                  onClick={() => setShowCreate(true)}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  Create Reseller
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && resellers.length > 0 && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing reseller partners…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Partners"
            value={filteredResellers.length}
            sub="Filtered reseller partners"
            icon={<BriefcaseBusiness className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Accounts"
            value={totals.accounts}
            sub={`${totals.connected} connected`}
            icon={<Wifi className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Available"
            value={totals.available}
            sub="Ready account inventory"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Total Bill"
            value={formatCurrency(totals.bill)}
            sub={`Paid ${formatCurrency(totals.paid)}`}
            icon={<CreditCard className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Balance"
            value={formatCurrency(totals.balance)}
            sub="Outstanding balance"
            icon={<Wallet className="h-5 w-5" />}
            tone={totals.balance > 0 ? "rose" : "emerald"}
          />
        </div>

        {/* Main Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[14px] font-black text-slate-900">
                Partner List
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Showing {filteredResellers.length} of {resellers.length}{" "}
                reseller partners
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
              <div className="relative w-full lg:w-96">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  className={`${inputCls} pl-10`}
                  placeholder="Search business, contact, phone, account..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="relative w-full lg:w-60">
                <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="INACTIVE">Inactive</option>
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              <button
                type="button"
                onClick={loadResellers}
                disabled={loading}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                <Search className="h-4 w-4" />
                Search
              </button>

              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Business</th>
                  <th className="px-5 py-3.5 font-black">Account</th>
                  <th className="px-5 py-3.5 font-black">Billing Period</th>
                  <th className="px-5 py-3.5 font-black">PPPoE Accounts</th>
                  <th className="px-5 py-3.5 font-black">Payments</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 text-right font-black">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : filteredResellers.length > 0 ? (
                  filteredResellers.map((item) => (
                    <tr
                      key={item.id}
                      className="cursor-pointer transition-colors hover:bg-emerald-50/60"
                      onClick={() => navigate(`/reseller-partners/${item.id}`)}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                            <BriefcaseBusiness className="h-4 w-4" />
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-black text-slate-900">
                              {item.business_name}
                            </p>

                            <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                              {item.contact_person || "No contact person"} •{" "}
                              {item.phone_number || "No phone"}
                            </p>

                            <p className="mt-0.5 flex items-center gap-1 truncate text-[11.5px] font-medium text-slate-500">
                              <MapPin className="h-3 w-3" />
                              {item.location || "No location"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <p className="font-mono text-[12.5px] font-black text-slate-900">
                          {item.account_number || "—"}
                        </p>

                        <p className="mt-0.5 text-[11.5px] font-bold text-slate-500">
                          Prefix: {item.username_prefix || "—"}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <div className="space-y-1">
                          <p className="flex items-center gap-1.5 text-[12px] font-bold text-slate-700">
                            <CalendarDays className="h-3.5 w-3.5 text-amber-600" />
                            Expires: {formatDate(item.billing_expires_at)}
                          </p>

                          <p className="flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500">
                            <Clock className="h-3.5 w-3.5" />
                            Grace ends: {formatDate(item.grace_ends_at)}
                          </p>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="space-y-1">
                          <p className="font-black text-slate-900">
                            {item.total_accounts || 0} total
                          </p>

                          <p className="text-[11.5px] font-medium text-slate-500">
                            {item.available_accounts || 0} available •{" "}
                            {item.connected_accounts || 0} connected
                          </p>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="space-y-1">
                          <p className="font-black text-slate-900">
                            {formatCurrency(item.balance)}
                          </p>

                          <p className="text-[11.5px] font-medium text-slate-500">
                            Paid: {formatCurrency(item.total_paid)}
                          </p>

                          <p className="text-[11.5px] font-medium text-slate-500">
                            Bill: {formatCurrency(item.total_bill)}
                          </p>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge status={item.status} />
                      </td>

                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/reseller-partners/${item.id}`);
                          }}
                          className={`${btnBase} h-9 border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <BriefcaseBusiness className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No reseller partners found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        Create a reseller partner or adjust your filters.
                      </p>

                      {hasFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filteredResellers.length > 0 ? (
              filteredResellers.map((item) => (
                <div key={item.id} className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-black text-slate-900">
                        {item.business_name}
                      </p>

                      <p className="mt-0.5 truncate text-[12px] font-medium text-slate-500">
                        {item.contact_person || "No contact"} •{" "}
                        {item.phone_number || "No phone"}
                      </p>
                    </div>

                    <StatusBadge status={item.status} />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                      <CreditCard className="h-3 w-3" />
                      {item.account_number || "No account"}
                    </span>

                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                      <MapPin className="h-3 w-3" />
                      {item.location || "No location"}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-sky-200 bg-sky-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">
                        Accounts
                      </p>

                      <p className="mt-1 text-[13px] font-black text-slate-900">
                        {item.total_accounts || 0} total
                      </p>

                      <p className="mt-0.5 text-[11px] font-bold text-slate-500">
                        {item.connected_accounts || 0} connected
                      </p>
                    </div>

                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-rose-700">
                        Balance
                      </p>

                      <p className="mt-1 text-[13px] font-black text-slate-900">
                        {formatCurrency(item.balance)}
                      </p>

                      <p className="mt-0.5 text-[11px] font-bold text-slate-500">
                        Paid {formatCurrency(item.total_paid)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                      Billing Expiry
                    </p>

                    <p className="mt-1 flex items-center gap-1.5 text-[12.5px] font-black text-slate-900">
                      <CalendarDays className="h-3.5 w-3.5 text-amber-600" />
                      {formatDate(item.billing_expires_at)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate(`/reseller-partners/${item.id}`)}
                    className={`${btnBase} mt-4 h-10 w-full border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200`}
                  >
                    <Eye className="h-4 w-4" />
                    View Details
                  </button>
                </div>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <BriefcaseBusiness className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No reseller partners found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Create a reseller partner or adjust your filters.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredResellers.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {resellers.length}
              </span>{" "}
              reseller partners
            </p>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
              <div>
                <p className="text-[15px] font-black text-slate-900">
                  Create Reseller Partner
                </p>

                <p className="mt-1 text-[12.5px] font-medium text-slate-500">
                  Add the business profile first. PPPoE accounts can be
                  generated after the partner is created.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form
              onSubmit={handleCreate}
              className="max-h-[calc(92vh-80px)] overflow-y-auto p-5"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Business Name
                  </label>

                  <input
                    className={inputCls}
                    value={form.business_name}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        business_name: e.target.value,
                      }))
                    }
                    placeholder="e.g. Quantum Link"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Contact Person
                  </label>

                  <input
                    className={inputCls}
                    value={form.contact_person}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        contact_person: e.target.value,
                      }))
                    }
                    placeholder="Main contact name"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Phone Number
                  </label>

                  <input
                    className={inputCls}
                    value={form.phone_number}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        phone_number: e.target.value,
                      }))
                    }
                    placeholder="2547..."
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Email
                  </label>

                  <input
                    className={inputCls}
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    placeholder="partner@example.com"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Location
                  </label>

                  <input
                    className={inputCls}
                    value={form.location}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        location: e.target.value,
                      }))
                    }
                    placeholder="Town / estate / area"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Grace Period Days
                  </label>

                  <input
                    className={inputCls}
                    type="number"
                    min={0}
                    value={form.grace_period_days}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        grace_period_days: Number(e.target.value || 0),
                      }))
                    }
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Billing Starts
                  </label>

                  <input
                    className={inputCls}
                    type="datetime-local"
                    value={formatDateInput(form.billing_started_at)}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        billing_started_at: e.target.value,
                      }))
                    }
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Billing Expires
                  </label>

                  <input
                    className={inputCls}
                    type="datetime-local"
                    value={formatDateInput(form.billing_expires_at)}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        billing_expires_at: e.target.value,
                      }))
                    }
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Payment Due
                  </label>

                  <input
                    className={inputCls}
                    type="datetime-local"
                    value={formatDateInput(form.payment_due_at)}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        payment_due_at: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Notes
                  </label>

                  <textarea
                    className={textareaCls}
                    rows={4}
                    value={form.notes}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Optional internal notes about this reseller partner..."
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  disabled={creating}
                  className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-700 hover:bg-slate-100`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={creating}
                  className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {creating ? "Creating…" : "Create Partner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
